import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { BodyColorService } from 'src/app/services/body-color/body-color.service';
import { BodyColorModalService } from 'src/app/services/body-color-modal/body-color-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-body-color',
  templateUrl: './body-color.component.html',
  styleUrls: ['./body-color.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class BodyColorComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  bodyColors: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedBodyColors: string[] = [];

  constructor(
    private _bodyColorService: BodyColorService,
    private _bodyColorModalService: BodyColorModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadBodyColors();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadBodyColors(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedBodyColors = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._bodyColorService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.bodyColors = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.bodyColors = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading body colors:', error);
          this.bodyColors = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadBodyColors(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadBodyColors(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadBodyColors();
  }

  openAddBodyColorModal(): void {
    this._bodyColorModalService.clearSelectedBodyColor();
    const modalElement = document.getElementById('kt_modal_body_color_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(bodyColor: any): void {
    this._bodyColorModalService.setSelectedBodyColor(bodyColor);
    const modalElement = document.getElementById('kt_modal_body_color_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteBodyColor(bodyColor: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${bodyColor.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(bodyColor);
      }
    });
  }

  private performDelete(bodyColor: any): void {
    const bodyColorId = bodyColor._id || bodyColor.id;
    this.subscriptions.push(
      this._bodyColorService.DeleteDataById(bodyColorId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Body color has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadBodyColors(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete body color.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting body color.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onBodyColorAdded(): void {
    console.log('Body color added, refreshing list...');
    this.loadBodyColors(this.currentPage, this.searchTerm, this.filters);
  }

  onBodyColorUpdated(): void {
    console.log('Body color updated, refreshing list...');
    this.loadBodyColors(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(bodyColorId: string): boolean {
    return this.selectedBodyColors.includes(bodyColorId);
  }

  isAllSelected(): boolean {
    return this.bodyColors.length > 0 && this.selectedBodyColors.length === this.bodyColors.length;
  }

  isIndeterminate(): boolean {
    return this.selectedBodyColors.length > 0 && this.selectedBodyColors.length < this.bodyColors.length;
  }

  toggleSelection(bodyColorId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedBodyColors.includes(bodyColorId)) {
        this.selectedBodyColors.push(bodyColorId);
      }
    } else {
      this.selectedBodyColors = this.selectedBodyColors.filter(id => id !== bodyColorId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedBodyColors = this.bodyColors.map(bodyColor => bodyColor._id);
    } else {
      this.selectedBodyColors = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-body-color-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-body-color-table-toolbar="selected"]');

    if (this.selectedBodyColors.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedBodyColors(): void {
    if (this.selectedBodyColors.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedBodyColors.length} selected body color(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedBodyColors.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._bodyColorService.BulkDelete(this.selectedBodyColors).subscribe({
        next: () => {
          this.loadBodyColors();
          this.selectedBodyColors = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected body colors have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting body colors:', error);
          Swal.fire('Error!', 'Failed to delete selected body colors.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
